import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SourcesLink } from "../components/SourcesLink";
import { ArrowRight } from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api } from "../lib/api";
import { AsOf, FxStamp } from "../components/AsOf";
import { VerifyChip } from "../components/DataLabel";
import { Reveal, useSeo } from "../lib/ui";
import { StageBar, StageClose } from "../components/StageChain";
import { DiasporaImpact } from "../components/DiasporaImpact";
import { GrassrootsValue } from "../components/GrassrootsValue";
import { HealthCase } from "../components/HealthCase";
import { LivePopulation } from "../components/LivePopulation";

const AXIS = { stroke: "#3f3f46", fontSize: 11, fontFamily: "JetBrains Mono, monospace" };

const ChartCard = ({ title, note, children, testId }) => (
  <div className="border border-white/10 bg-[#0b0b0b] p-6 md:p-8" data-testid={testId}>
    <p className="overline text-ochre">{title}</p>
    {note ? <p className="mt-3 text-sm text-zinc-500">{note}</p> : null}
    <div className="mt-7 h-[260px] w-full">{children}</div>
  </div>
);

const tip = {
  contentStyle: {
    background: "#0b0b0b",
    border: "1px solid #E32118",
    borderRadius: 0,
    fontFamily: "JetBrains Mono, monospace",
    fontSize: 12,
  },
  labelStyle: { color: "#E5A91E" },
};



const SOURCE_FOR = {
  GDP: ["World Bank Open Data", "https://data.worldbank.org/indicator/NY.GDP.MKTP.CD?locations=KE", "official", "verified"],
  "GDP (PPP)": ["World Bank Open Data", "https://data.worldbank.org/indicator/NY.GDP.MKTP.PP.CD?locations=KE", "official", "verified"],
  "Diaspora remittances": ["Central Bank of Kenya", "https://www.centralbank.go.ke/diaspora-remittances/", "official", "official"],
  "Betting via M-Pesa": ["Safaricom / BCLB", "https://www.safaricom.co.ke/investor-relation/financial-reporting", "official", "official"],
};

export default function Money() {
  const [data, setData] = useState({ series: [], sources: [], argument: [], note: "" });

  useSeo({
    title: "The Money — KickStartKenyaFootball",
    description:
      "Kenya's financial picture behind the football plan: GDP and GDP PPP, sports betting spend on M-Pesa, diaspora remittances and private equity from 2022 to 2026, with projections to 2031.",
  });

  useEffect(() => {
    api.get("/finance").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const series = useMemo(() => data.series || [], [data.series]);
  const historic = useMemo(() => series.filter((r) => r.year <= 2026), [series]);
  const projected = useMemo(() => series.filter((r) => r.year >= 2026), [series]);
  const latest = historic[historic.length - 1] || {};
  const KSH_PER_USD = data.fx?.kes_per_usd || 129;
  const betting = historic.map((r) => ({ ...r, usd: +(r.betting_mpesa_ksh_bn / KSH_PER_USD).toFixed(2) }));
  const usd = (ksh) => (ksh ? `$${(ksh / KSH_PER_USD).toFixed(2)}bn` : "—");

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="money-page">
      <StageBar stage="invest" />
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <Reveal>
          <p className="eyebrow">Stage three — paying for it</p>
          <h1 className="display-1 mt-5 max-w-4xl">Kenya Can Afford This</h1>
          <p className="body-copy mt-6 max-w-3xl">
            The recommended steps add up to a fraction of what Kenya already moves every year through betting slips and
            diaspora transfers. This is not a request for charity: it is a young, fast-growing market — a median age of
            20, a population passing 58 million, and a football economy recorded at close to nothing — where the same
            money currently leaving the country through betting could build permanent assets that return wages, VAT,
            payroll tax, tourism receipts and export-grade players. Below is the national picture, every figure sourced
            and every estimate labelled for challenge.
          </p>
        </Reveal>

        <div className="mt-12">
          <LivePopulation pop={data.population} />
        </div>

        <div className="mt-12 grid gap-px border border-zinc-800 bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4" data-testid="money-headlines">
          {[
            ["GDP", latest.gdp_usd_bn ? `$${latest.gdp_usd_bn}bn` : "—", `${latest.year || ""} ${latest.status || ""}`],
            ["GDP (PPP)", latest.gdp_ppp_usd_bn ? `$${latest.gdp_ppp_usd_bn}bn` : "—", "Purchasing power parity"],
            [
              "Diaspora remittances",
              latest.remittances_usd_bn ? `$${latest.remittances_usd_bn}bn` : "—",
              "Largest source of forex",
            ],
            [
              "Betting via M-Pesa",
              latest.betting_mpesa_ksh_bn ? `KSh ${latest.betting_mpesa_ksh_bn}bn` : "—",
              latest.betting_mpesa_ksh_bn
                ? `${usd(latest.betting_mpesa_ksh_bn)} USD · staked in one year`
                : "Staked in one year",
            ],
          ].map(([label, value, note], i) => (
            <Reveal key={label} delay={i * 0.05}>
              <div className="h-full bg-[#0b0b0b] p-7" data-testid={`money-headline-${i}`}>
                <p className="text-[0.58rem] font-bold uppercase tracking-[0.2em] text-zinc-400">{label}</p>
                <p className="mt-4 font-display text-3xl font-bold leading-none text-white md:text-4xl">{value}</p>
                <p className="mt-3 text-[0.58rem] uppercase tracking-[0.16em] text-ochre">{note}</p>
                <span className="mt-3 block">
                  <VerifyChip
                    v={latest.status === "actual" ? (SOURCE_FOR[label] || [])[3] : "model"}
                    testId={`money-headline-label-${i}`}
                  />
                </span>
                <AsOf
                  testId={`money-headline-source-${i}`}
                  publisher={(SOURCE_FOR[label] || [])[0]}
                  url={(SOURCE_FOR[label] || [])[1]}
                  kind={latest.status === "actual" ? (SOURCE_FOR[label] || [])[2] : "estimate"}
                  asOf={latest.updated_at || (latest.year ? String(latest.year) : "")}
                />
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-12 grid gap-px border border-zinc-800 bg-zinc-800 lg:grid-cols-2">
          <ChartCard
            title="GDP and GDP (PPP)"
            note="US$ billions. 2025–2026 estimated, 2027–2031 projected."
            testId="money-chart-gdp"
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series}>
                <CartesianGrid stroke="#1c1c1c" vertical={false} />
                <XAxis dataKey="year" {...AXIS} />
                <YAxis {...AXIS} />
                <Tooltip {...tip} />
                <Area type="monotone" dataKey="gdp_ppp_usd_bn" name="GDP PPP $bn" stroke="#E5A91E" fill="#E5A91E" fillOpacity={0.16} strokeWidth={2} />
                <Area type="monotone" dataKey="gdp_usd_bn" name="GDP $bn" stroke="#E32118" fill="#E32118" fillOpacity={0.2} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Sports betting staked through M-Pesa"
            note={`KSh billions per year, with USD equivalents at the live rate of KSh ${Math.round(KSH_PER_USD)} to the dollar. Every bar is more than the whole build programme costs.`}
            testId="money-chart-betting"
          >
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={betting}>
                <CartesianGrid stroke="#1c1c1c" vertical={false} />
                <XAxis dataKey="year" {...AXIS} />
                <YAxis yAxisId="ksh" {...AXIS} />
                <YAxis yAxisId="usd" orientation="right" {...AXIS} />
                <Tooltip {...tip} />
                <Legend wrapperStyle={{ fontFamily: "JetBrains Mono, monospace", fontSize: 10 }} />
                <Bar yAxisId="ksh" dataKey="betting_mpesa_ksh_bn" name="KSh bn staked">
                  {betting.map((r) => (
                    <Cell key={r.year} fill={r.status === "actual" ? "#E32118" : "#6b2018"} />
                  ))}
                </Bar>
                <Line yAxisId="usd" type="monotone" dataKey="usd" name="USD bn equivalent" stroke="#E5A91E" strokeWidth={2.5} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Diaspora remittances and private equity"
            note="US$ billions. Remittances dwarf every other inbound flow."
            testId="money-chart-inflows"
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={historic}>
                <CartesianGrid stroke="#1c1c1c" vertical={false} />
                <XAxis dataKey="year" {...AXIS} />
                <YAxis {...AXIS} />
                <Tooltip {...tip} />
                <Line type="monotone" dataKey="remittances_usd_bn" name="Remittances $bn" stroke="#E5A91E" strokeWidth={2.5} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="private_equity_usd_bn" name="Private equity $bn" stroke="#ffffff" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Population and projections"
            note="Millions. 2019 census base, re-anchored on the live Worldometer estimate and projected to 2031."
            testId="money-chart-population"
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series}>
                <CartesianGrid stroke="#1c1c1c" vertical={false} />
                <XAxis dataKey="year" {...AXIS} />
                <YAxis {...AXIS} domain={["dataMin - 2", "dataMax + 2"]} />
                <Tooltip {...tip} />
                <Area type="monotone" dataKey="population_m" name="Population m" stroke="#4ea165" fill="#4ea165" fillOpacity={0.18} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <div className="mt-16">
          <DiasporaImpact series={series} />
        </div>

        <div className="mt-16">
          <p className="eyebrow">Year by year</p>
          <h2 className="display-2 mt-4">The full table</h2>
          <div className="no-scrollbar mt-8 overflow-x-auto border border-white/10">
            <table className="w-full min-w-[960px] text-left" data-testid="money-table">
              <thead>
                <tr className="border-b border-white/15 bg-[#0b0b0b]">
                  {["Year", "GDP $bn", "GDP PPP $bn", "Population m", "Remittances $bn", "Betting KSh bn", "Betting $bn", "PE $bn", "Status"].map(
                    (h) => (
                      <th key={h} className="px-4 py-3 text-[0.65rem] font-bold uppercase tracking-[0.18em] text-zinc-300">
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {series.map((r) => (
                  <tr key={r.year} className="border-b border-white/5" data-testid={`money-row-${r.year}`}>
                    <td className="mono px-4 py-3 text-sm text-ochre">{r.year}</td>
                    <td className="mono px-4 py-3 text-sm text-white">{r.gdp_usd_bn}</td>
                    <td className="mono px-4 py-3 text-sm text-white">{r.gdp_ppp_usd_bn}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{r.population_m}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{r.remittances_usd_bn}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{r.betting_mpesa_ksh_bn}</td>
                    <td className="mono px-4 py-3 text-sm text-ochre">
                      {(r.betting_mpesa_ksh_bn / KSH_PER_USD).toFixed(2)}
                    </td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{r.private_equity_usd_bn}</td>
                    <td className="px-4 py-3 text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500">{r.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 max-w-3xl text-[0.62rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-400">
            {data.note}
          </p>
        </div>

        <div className="mt-16 grid gap-px border border-zinc-800 bg-zinc-800 md:grid-cols-3" data-testid="money-argument">
          {(data.argument || []).map(([title, body], i) => (
            <Reveal key={title} delay={i * 0.06}>
              <div className="h-full bg-[#0b0b0b] p-8">
                <span className="mono text-xs text-maasai">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-6 font-display text-lg uppercase leading-tight tracking-tight text-white md:text-xl">
                  {title}
                </h3>
                <p className="body-copy mt-4">{body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <GrassrootsValue />
        <HealthCase />

        <div className="mt-14">
          <p className="overline">Sources</p>
          <FxStamp fx={data.fx} testId="money-fx-stamp" />
          <SourcesLink testId="money-sources-link" />
          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="money-sources">            {(data.sources || []).map((s) => (
              <a
                key={s.label}
                href={s.url}
                target="_blank"
                rel="noreferrer"
                className="group border border-white/10 p-5 transition-colors hover:border-ochre"
              >
                <p className="text-[0.58rem] font-bold uppercase tracking-[0.18em] text-zinc-400">{s.label}</p>
                <p className="mt-2 text-sm text-zinc-300 transition-colors group-hover:text-ochre">{s.source}</p>
              </a>
            ))}
          </div>
        </div>

        <div className="mt-14 flex flex-wrap gap-4">
          <Link to="/apply" className="btn-primary" data-testid="money-proposal-cta">
            Tell us about an opportunity <ArrowRight size={14} />
          </Link>
          <Link to="/counties" className="btn-ghost">
            County map
          </Link>
          <Link to="/plans" className="btn-ghost">
            Building a football ecosystem
          </Link>
        </div>
      </div>
      <StageClose stage="invest" />
    </div>
  );
}
